import { OverlayToaster, Position } from "@blueprintjs/core";
import { segmentTrackRestudyCreated } from "analytics/analyticTrackEvents";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import {
    useGetQueueProjectHopsLazyQuery,
    useGetQueueProjectsLazyQuery,
    useGetRelatedQueueProjectsLazyQuery,
    useGetScopesWithQueueProjectsLazyQuery
} from "generated/graphql";
import { DateTime } from "luxon";
import { useState } from "react";
import {
    compareRelatedRows,
    getScoreBucket,
    QueueProjectRelatedness,
    toQueueProjectRelatedness
} from "types/queueProjectRelatednessType";
import {
    convertHasuraQueueProjectsByFuelType,
    QueueProject
} from "types/queueProjectsType";
import {
    getBaseScopesForFuelTypes,
    isLocalStudyBaseEligible,
    MISO_2022_REALISTIC_SCOPE_TYPE,
    Scope
} from "types/scope";
import {
    getAllCapacityCostsScopeViewsForRegion,
    getCapacityCostsScopeViewFromRegionAndGenerator,
    getGenerationFuelTypesForRegion
} from "types/scopeViewType";
import { validateQueueProjectsByFuelType } from "utils/queueProjectUtils";
import {
    useCreateUserScope,
    ValidatedNewScopeRequest
} from "./useCreateUserScope";

const AutoStressToaster = OverlayToaster.create({
    position: Position.TOP
});

const AUTO_STRESS_MAX_ADDED_MW = 5000;
const AUTO_STRESS_MAX_HOPS = 7;

/**
 * Ordered priority for auto-stress to turn on projects from. First dominant, then 1 hop,
 * then high, then 2-3 hops, then medium/low, then 4-7 hops.
 */
const getAutoStressTier = (
    score: number | undefined,
    numHops: number | undefined
): number | undefined => {
    const bucket = score === undefined ? undefined : getScoreBucket(score);
    if (bucket === "dominant") {
        return 0;
    }
    if (numHops !== undefined && numHops <= 1) {
        return 1;
    }
    if (bucket === "high") {
        return 2;
    }
    if (numHops !== undefined && numHops <= 3) {
        return 3;
    }
    if (bucket === "medium" || bucket === "low") {
        return 4;
    }
    if (numHops !== undefined && numHops <= AUTO_STRESS_MAX_HOPS) {
        return 5;
    }
    return undefined;
};

/**
 * Turn on queue projects in order of tiers above. Adds at most AUTO_STRESS_MAX_ADDED_MW
 * in total, and doesn't touch any already-included projects.
 */
const applyAutoStressInclusion = (
    baseProjects: ReadonlyArray<QueueProject>,
    relatedness: QueueProjectRelatedness
) => {
    const candidates: Array<{
        project: QueueProject;
        tier: number;
        projectNumber: string;
        score: number | undefined;
        numHops: number | undefined;
    }> = [];
    for (const project of baseProjects) {
        if (project.included) {
            continue;
        }
        const { projectNumber } = project;
        const score = relatedness.scoreByProjectNumber.get(projectNumber);
        const numHops = relatedness.hopsByProjectNumber.get(projectNumber);
        const tier = getAutoStressTier(score, numHops);
        if (tier !== undefined) {
            candidates.push({ project, tier, projectNumber, score, numHops });
        }
    }
    candidates.sort((a, b) =>
        a.tier === b.tier
            ? compareRelatedRows(a, b, true, true)
            : a.tier - b.tier
    );

    const addedProjects: Array<QueueProject> = [];
    let addedMw = 0;
    for (const { project } of candidates) {
        if (addedMw + project.erisCapacityMw > AUTO_STRESS_MAX_ADDED_MW) {
            // Once any project would send us over, stop
            break;
        }
        addedMw += project.erisCapacityMw;
        addedProjects.push(project);
    }

    const editedQueueProjects = baseProjects.map((project) => ({
        ...project,
        included:
            project.included ||
            addedProjects.some(
                (other) => other.projectNumber === project.projectNumber
            )
    }));
    return { editedQueueProjects, addedProjects };
};

const buildAutoStressDescription = (
    baseScope: Scope,
    addedProjects: ReadonlyArray<QueueProject>
): string => {
    const totalMw = addedProjects.reduce((sum, p) => sum + p.erisCapacityMw, 0);
    const heading =
        `Automatically-generated stressed study based on ${baseScope.display_name}, additionally adding queued generation in order of impact/closeness, up to a cap.\n\n` +
        `This study included **${addedProjects.length} additional queue project` +
        `${addedProjects.length === 1 ? "" : "s"} ` +
        `(${Math.round(totalMw).toLocaleString()} MW)**:`;
    const rows = addedProjects.map(
        (p) =>
            `* ${p.projectNumber}: ${Math.round(
                p.erisCapacityMw
            ).toLocaleString()} MW ${p.fuelType} in ${p.subRegion}`
    );
    const dataFreshnessAtTimeOfCreation = `\n\nData freshness at time of creation: \n\n${baseScope.data_freshness_markdown}`;
    return [heading, "", ...rows, dataFreshnessAtTimeOfCreation].join("\n");
};

const showError = (message: string): undefined => {
    AutoStressToaster.show({ message, intent: "danger" });
    return undefined;
};

/**
 * Auto-create a local study with impactful queue projects turned on.
 * Automatically chooses the first scope in the dropdown, uses all fuel types
 */
export const useAutoStressBus = () => {
    const { bus, niraBusId, allScopesForBus, scopeToFuelTypesForBus } =
        useBusPageConfiguration();
    const { createUserScope } = useCreateUserScope();

    const [getScopesWithQueueProjects] =
        useGetScopesWithQueueProjectsLazyQuery();
    const [getQueueProjects] = useGetQueueProjectsLazyQuery();
    const [getRelatedQueueProjects] = useGetRelatedQueueProjectsLazyQuery();
    const [getQueueProjectHops] = useGetQueueProjectHopsLazyQuery();

    const [isAutoStressing, setIsAutoStressing] = useState(false);

    const prepare = async (): Promise<ValidatedNewScopeRequest | undefined> => {
        const region = bus.region;
        const fuelTypes = getGenerationFuelTypesForRegion(region);
        if (fuelTypes.length === 0) {
            return showError(
                `${region} has no generation fuel types to study.`
            );
        }

        const eligibleBaseScopes = allScopesForBus.filter(
            isLocalStudyBaseEligible
        );
        const scopesResult = await getScopesWithQueueProjects({
            variables: {
                scopes: eligibleBaseScopes.map((s) => s.scope),
                scope_views: [
                    ...getAllCapacityCostsScopeViewsForRegion(region)
                ],
                region
            }
        });
        if (scopesResult.error !== undefined) {
            return showError("Failed to load scope data. Please try again.");
        }

        const baseScope = getBaseScopesForFuelTypes(
            eligibleBaseScopes,
            scopeToFuelTypesForBus,
            scopesResult.data,
            fuelTypes,
            region
        )[0];
        if (baseScope === undefined) {
            return showError(
                "No base scope at this bus covers every fuel type it is studied for."
            );
        }

        const scopeViews = Array.from(
            new Set(
                fuelTypes.map((ft) =>
                    getCapacityCostsScopeViewFromRegionAndGenerator(region, ft)
                )
            )
        );
        const sortScopeView = getCapacityCostsScopeViewFromRegionAndGenerator(
            region,
            fuelTypes[0]
        );

        const [queueProjectsResult, relatednessResult, hopsResult] =
            await Promise.all([
                getQueueProjects({
                    variables: {
                        scope: baseScope.scope,
                        scope_views: scopeViews,
                        region
                    }
                }),
                getRelatedQueueProjects({
                    variables: {
                        nira_bus_id: niraBusId,
                        scope: MISO_2022_REALISTIC_SCOPE_TYPE,
                        scope_view: sortScopeView,
                        region
                    }
                }),
                getQueueProjectHops({
                    variables: {
                        nira_bus_id: niraBusId,
                        scope_view: sortScopeView,
                        region
                    }
                })
            ]);

        if (queueProjectsResult.data === undefined) {
            return showError(
                "Failed to load the base scope's queue generators. Please try again."
            );
        }

        const queueProjectsByFuelType = convertHasuraQueueProjectsByFuelType(
            queueProjectsResult.data,
            fuelTypes,
            region
        );
        const validation = validateQueueProjectsByFuelType(
            queueProjectsByFuelType
        );
        if (!validation.valid) {
            return showError(validation.message);
        }

        // A failed impact or hops query has no data, so it only costs coverage.
        const relatedness = toQueueProjectRelatedness(
            relatednessResult.data,
            hopsResult.data
        );

        // Every fuel type carries the same project set, which
        // validateQueueProjectsByFuelType just confirmed.
        const baseProjects =
            Array.from(queueProjectsByFuelType.values())[0] ?? [];

        const { editedQueueProjects, addedProjects } = applyAutoStressInclusion(
            baseProjects,
            relatedness
        );
        if (addedProjects.length === 0) {
            return showError(
                `Auto-stress found no queue projects to turn on: none related to this bus is switched off in the base scope, or the highest-priority one alone exceeds the ${AUTO_STRESS_MAX_ADDED_MW.toLocaleString()} MW cap`
            );
        }

        return {
            displayName: `Auto-Stress ${DateTime.now().toFormat(
                "MM/dd/yyyy HH:mm:ss"
            )}`,
            notes: buildAutoStressDescription(baseScope, addedProjects),
            selectedFuelTypes: fuelTypes,
            baseScope,
            editedQueueProjects,
            newProjects: []
        };
    };

    const autoStressBus = async (): Promise<void> => {
        setIsAutoStressing(true);
        const input = await prepare();
        const created =
            input !== undefined &&
            (await createUserScope(input, "auto_stress"));
        segmentTrackRestudyCreated(
            niraBusId,
            bus.region,
            "auto_stress",
            created
        );
        setIsAutoStressing(false);
    };

    return { autoStressBus, isAutoStressing };
};
